/**
 * Shared pieces of the member door (TRUS-778): the public member home, /help,
 * and the /member/join sign-up. Phone-first, plain backgrounds, teal accent,
 * 44px+ targets.
 */
import React from 'react';
import {
  Utensils,
  Home,
  Receipt,
  Bus,
  HeartPulse,
  GraduationCap,
  Baby,
  ShieldCheck,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { leaveSite, type NeedCode } from '@/lib/memberJoin';
import { useMemberHelpLine } from '@/lib/memberHelpLine';

export const NEED_ICONS: Record<NeedCode, LucideIcon> = {
  food: Utensils,
  housing: Home,
  bills: Receipt,
  transportation: Bus,
  health: HeartPulse,
  work_school: GraduationCap,
  family: Baby,
  safety: ShieldCheck,
  not_sure: HelpCircle,
};

/** Languages offered on the member door. Codes match the translation context. */
const DOOR_LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'zh', name: '中文' },
  { code: 'tl', name: 'Tagalog' },
];

/** Primary (filled teal) action: 52–56px tall, full width on phones. */
export const DOOR_PRIMARY =
  'inline-flex items-center justify-center gap-2 min-h-[56px] px-6 rounded-[10px] bg-v2accent text-white text-lg font-semibold hover:bg-v2accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
/** Secondary (teal outline) action. */
export const DOOR_SECONDARY =
  'inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-[10px] border border-v2accent text-v2accent-ink font-semibold hover:bg-v2accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent';
/** Quiet (paper) action. */
export const DOOR_QUIET =
  'inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-[10px] bg-paper-2 text-ink font-semibold hover:bg-v2border-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent';
/** Text input. */
export const DOOR_INPUT =
  'w-full min-h-[48px] px-3 rounded-lg border border-v2border bg-surface text-ink text-base placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-v2accent focus:border-v2accent';

/** Language select bound to the translation context. */
export function DoorLanguageSelect({ id = 'door-language' }: { id?: string }) {
  const { t, language, setLanguage } = useAppTranslation();
  const current = (language || 'en').split('-')[0];
  const options = DOOR_LANGUAGES.some((l) => l.code === current)
    ? DOOR_LANGUAGES
    : [...DOOR_LANGUAGES, { code: current, name: current }];
  return (
    <div className="flex items-center gap-2 text-ink">
      <label htmlFor={id} className="text-sm">
        {t('memberDoor.language', 'Language')}
      </label>
      <select
        id={id}
        value={current}
        onChange={(e) => {
          void setLanguage(e.target.value);
        }}
        className="min-h-[44px] px-2 rounded-md border border-v2border bg-surface text-ink text-sm"
      >
        {options.map((l) => (
          <option key={l.code} value={l.code}>
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** "Leave this site": quick exit, replaces history so Back does not return. */
export function LeaveSiteButton({ compact = false }: { compact?: boolean }) {
  const { t } = useAppTranslation();
  return (
    <button
      type="button"
      onClick={leaveSite}
      className="inline-flex items-center min-h-[44px] px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-700"
    >
      {compact ? t('memberDoor.leave', 'Leave') : t('memberDoor.leave_this_site', 'Leave this site')}
    </button>
  );
}

/** Thin top bar: language on the left, quick exit on the right. */
export function DoorUtilityBar({ showHelpLine = false }: { showHelpLine?: boolean }) {
  const { t } = useAppTranslation();
  const helpLine = useMemberHelpLine();
  return (
    <div className="bg-paper-2 border-b border-v2border">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-2 text-sm">
        <DoorLanguageSelect />
        <div className="flex items-center gap-4">
          {showHelpLine && helpLine && (
            <span className="hidden md:inline text-ink-700">
              {t('memberDoor.want_to_talk', 'Want to talk to someone?')}{' '}
              <a href={helpLine.sms} className="font-semibold text-v2accent-ink hover:underline">
                {t('memberDoor.text', 'Text')}
              </a>{' '}
              {t('memberDoor.or', 'or')}{' '}
              <a href={helpLine.tel} className="font-semibold text-v2accent-ink hover:underline">
                {t('memberDoor.call', 'call')} {helpLine.display}
              </a>
            </span>
          )}
          <LeaveSiteButton />
        </div>
      </div>
    </div>
  );
}

/** One line under every sign-up step: "Need help? Text or call <help line>". */
export function HelpLineFooter() {
  const { t } = useAppTranslation();
  const helpLine = useMemberHelpLine();
  if (!helpLine) return null;
  return (
    <p className="text-sm text-ink-500 text-center">
      {t('memberDoor.need_help_text_or_call', 'Need help? Text or call')}{' '}
      <a href={helpLine.tel} className="font-semibold text-v2accent-ink hover:underline">
        {helpLine.display}
      </a>
    </p>
  );
}

/** "Rather talk to a person?" block with Text / Call buttons. */
export function TalkToAPerson({ className = '' }: { className?: string }) {
  const { t } = useAppTranslation();
  const helpLine = useMemberHelpLine();
  if (!helpLine) return null;
  return (
    <section className={`flex flex-col gap-3 ${className}`}>
      <h2 className="font-display text-xl font-semibold text-ink">
        {t('memberDoor.rather_talk', 'Rather talk to a person?')}
      </h2>
      <p className="text-ink-700">
        {t('memberDoor.navigator_answers', 'Text or call us. A navigator answers, in your language.')}
      </p>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <a href={helpLine.sms} className={DOOR_SECONDARY}>
          {t('memberDoor.text', 'Text')} {helpLine.display}
        </a>
        <a href={helpLine.tel} className={DOOR_SECONDARY}>
          {t('memberDoor.call_cap', 'Call')} {helpLine.display}
        </a>
      </div>
    </section>
  );
}

/** The always-present crisis line. `prominent` is the urgent version. */
export function CrisisLine({ prominent = false }: { prominent?: boolean }) {
  const { t } = useAppTranslation();
  if (prominent) {
    return (
      <div role="alert" className="rounded-xl border-2 border-danger bg-danger-soft p-4 flex flex-col gap-3">
        <p className="font-semibold text-danger-ink">
          {t('memberDoor.crisis_heading', 'If you are in danger or need help right now')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <a
            href="tel:911"
            className="inline-flex items-center justify-center min-h-[52px] rounded-[10px] bg-danger text-white font-semibold"
          >
            {t('memberDoor.call_911', 'Call 911')}
          </a>
          <a
            href="tel:988"
            className="inline-flex items-center justify-center min-h-[52px] rounded-[10px] border-2 border-danger text-danger-ink font-semibold bg-surface"
          >
            {t('memberDoor.call_988', 'Call or text 988')}
          </a>
        </div>
        <p className="text-sm text-danger-ink">
          {t(
            'memberDoor.crisis_body',
            '988 is free, private and open all day and night, for anyone thinking about hurting themselves or in crisis.',
          )}
        </p>
      </div>
    );
  }
  return (
    <p className="text-sm text-ink-700">
      {t('memberDoor.in_danger', 'In danger right now? Call')}{' '}
      <a href="tel:911" className="font-semibold text-v2accent-ink underline">
        911
      </a>
      . {t('memberDoor.thinking_hurting', 'Thinking about hurting yourself? Call or text')}{' '}
      <a href="tel:988" className="font-semibold text-v2accent-ink underline">
        988
      </a>
      {t('memberDoor.any_time', ', any time.')}
    </p>
  );
}

/** Wordmark at the sizes the marketing header uses. */
export function DoorLogo() {
  return <img src="/weavein-wordmark.png" alt="WeaveIn" className="h-8 lg:h-10 xl:h-11 w-auto shrink-0" />;
}

export const DoorSection: React.FC<{ className?: string; narrow?: boolean; children: React.ReactNode }> = ({
  className = '',
  narrow = false,
  children,
}) => <div className={`${narrow ? 'max-w-2xl' : 'max-w-6xl'} w-full mx-auto px-4 ${className}`}>{children}</div>;
